// AI ダイジェスト ハブ — 技術アプリの入口。既存の 2 種類のダイジェスト
// (週間プロジェクト / 日報) を集約し、送信フローを一貫化するための土台。
// 現時点では既存ページへの導線 + 「今後の送信確認フロー」の説明ラベル。
// Phase 3 (confirm-send 機能) は別ターンで実装。
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Envelope, ChartLineUp, ListChecks, Gear, ArrowRight, ClockCounterClockwise, CheckCircle, XCircle, ArrowsClockwise, Warning, Paperclip, PaperPlaneTilt } from '@phosphor-icons/react';
import { useAuth } from '@/auth/AuthContext';
import ConfirmSendDialog from '@/components/digest/ConfirmSendDialog';
import ProgressReportWizard from '@/components/digest/ProgressReportWizard';

const API = process.env.REACT_APP_BACKEND_URL;

function Card({ Icon, tone, title, subtitle, to, cta, testId }) {
  const bg = { violet: 'from-violet-50 to-indigo-50 border-violet-200', teal: 'from-teal-50 to-cyan-50 border-teal-200', amber: 'from-amber-50 to-yellow-50 border-amber-200', zinc: 'from-zinc-50 to-slate-50 border-zinc-200' }[tone] || 'from-zinc-50 to-slate-50 border-zinc-200';
  const iconTone = { violet: 'text-violet-600 bg-violet-100', teal: 'text-teal-600 bg-teal-100', amber: 'text-amber-600 bg-amber-100', zinc: 'text-zinc-600 bg-zinc-100' }[tone] || 'text-zinc-600 bg-zinc-100';
  return (
    <Link to={to} data-testid={testId}
      className={`group flex flex-col gap-3 rounded-lg border bg-gradient-to-br ${bg} p-5 transition-all hover:shadow-md hover:-translate-y-0.5`}>
      <div className="flex items-center gap-3">
        <div className={`rounded-xl p-2.5 ${iconTone}`}><Icon size={22} weight="duotone" /></div>
        <div className="min-w-0">
          <h3 className="text-[15px] font-semibold text-zinc-900">{title}</h3>
          <p className="text-[11.5px] text-zinc-500 mt-0.5 line-clamp-2">{subtitle}</p>
        </div>
      </div>
      <div className="flex items-center justify-end text-[12px] font-semibold text-zinc-700 group-hover:text-zinc-900">
        {cta} <ArrowRight size={14} className="ml-1 transition-transform group-hover:translate-x-0.5" />
      </div>
    </Link>
  );
}

export default function EngAiDigest() {
  const { user } = useAuth();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardRun, setWizardRun] = useState(null);
  const [historyRefresh, setHistoryRefresh] = useState(0);

  const isAdmin = user?.role === 'admin';

  return (
    <div className="space-y-5" data-testid="eng-ai-digest">
      <header>
        <h1 className="text-xl font-semibold tracking-tight text-zinc-900 flex items-center gap-2">
          <Envelope size={22} weight="duotone" className="text-teal-600" /> AIダイジェスト
        </h1>
        <p className="text-[12.5px] text-zinc-500 mt-1">
          週間プロジェクト・日報のAIサマリーを生成し、確認のうえメール送信します。
          送信前に必ずプレビューと宛先を確認してください。
        </p>
      </header>

      {/* 進捗レポート送信ウィザード — 部署 → 週 → AI生成 → プレビュー → 宛先 → 送信 */}
      <div className="rounded-lg border border-teal-200 bg-teal-700 p-5 text-white flex flex-wrap items-center gap-4"
        data-testid="progress-report-cta">
        <div className="rounded-xl bg-white/15 p-3"><PaperPlaneTilt size={26} weight="duotone" /></div>
        <div className="min-w-0 flex-1">
          <h2 className="text-[15px] font-bold">進捗レポートを送信</h2>
          <p className="text-[12px] text-teal-50/90 mt-0.5 max-w-xl">
            部署と週を選ぶだけ — AIがプロジェクト進捗＋日報ハイライトをまとめ、プレビュー確認後にPDF添付で送信（例: 社長宛て）。
            <span className="font-semibold">自動送信はありません</span> — 送信は毎回この操作が必要です
          </p>
        </div>
        <button type="button" onClick={() => setWizardOpen(true)}
          data-testid="progress-report-open-wizard"
          className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-white text-teal-700 px-4 py-2 text-[12.5px] font-bold hover:bg-teal-50 transition-colors shadow-sm">
          ウィザードを開く <ArrowRight size={14} weight="bold" />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card
          Icon={ChartLineUp} tone="violet"
          title="週間プロジェクトダイジェスト"
          subtitle="Plannerの進行中プロジェクト・タスク・リスクをまとめた週次レポート"
          to="/planner/digest"
          cta="生成・送信"
          testId="digest-card-project"
        />
        <Card
          Icon={ListChecks} tone="teal"
          title="日報ダイジェスト"
          subtitle="技術メンバーの週間日報をエンジニア別/プロジェクト別/経営向けに要約"
          to="/engineering/reports/digests"
          cta="生成・送信"
          testId="digest-card-daily"
        />
        {isAdmin && (
          <Card
            Icon={Gear} tone="zinc"
            title="日報ダイジェストの送信設定"
            subtitle="自動生成の曜日/時刻と宛先。進捗レポートは対象外"
            to="/engineering/reports/digests"
            cta="設定を開く"
            testId="digest-card-settings"
          />
        )}
      </div>

      <SendHistorySection refreshToken={historyRefresh} />

      <ProgressReportWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onPrepared={(run) => { setWizardOpen(false); setWizardRun(run); }}
      />
      <ConfirmSendDialog
        run={wizardRun}
        onClose={() => { setWizardRun(null); setHistoryRefresh((v) => v + 1); }}
        onSent={() => { toast.success('進捗レポートを送信しました'); setWizardRun(null); setHistoryRefresh((v) => v + 1); }}
      />
    </div>
  );
}


const STATUS_STYLE = {
  sent:                 { label: '送信済み',   Icon: CheckCircle,      cls: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
  failed:               { label: '失敗',       Icon: XCircle,          cls: 'bg-rose-50 border-rose-200 text-rose-700' },
  cancelled:            { label: 'キャンセル', Icon: XCircle,          cls: 'bg-zinc-100 border-zinc-200 text-zinc-600' },
  pending_confirmation: { label: '確認待ち',   Icon: ArrowsClockwise,  cls: 'bg-amber-50 border-amber-200 text-amber-700' },
  confirmed:            { label: '確認済み',   Icon: CheckCircle,      cls: 'bg-indigo-50 border-indigo-200 text-indigo-700' },
  draft:                { label: '下書き',     Icon: ClockCounterClockwise, cls: 'bg-zinc-50 border-zinc-200 text-zinc-500' },
};

const KIND_LABEL = { project_weekly: '週間プロジェクト', daily_report: '日報', eng_progress: '進捗レポート' };

const fmtDT = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  } catch (_e) { return iso; }
};

function SendHistorySection({ refreshToken = 0 }) {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState('');
  const [dlgRun, setDlgRun] = useState(null);

  const load = () => {
    axios.get(`${API}/api/digest-runs`, {
      params: filter ? { limit: 50, kind: filter } : { limit: 50 },
      withCredentials: true,
    }).then((r) => setRows(r.data.runs || []))
      .catch(() => setRows([]));
  };

  useEffect(load, [filter, refreshToken]);

  const openDialog = async (row) => {
    // Fetch the full row (list endpoint strips markdown_snapshot & email_body)
    try {
      const r = await axios.get(`${API}/api/digest-runs/${row.id}`, { withCredentials: true });
      setDlgRun(r.data);
    } catch (_e) {
      setDlgRun(row);   // graceful fallback — preview will still fetch on-demand
    }
  };

  return (
    <div className="rounded-xl border border-zinc-200 bg-white" data-testid="digest-history">
      <div className="px-4 py-3 border-b border-zinc-200 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <ClockCounterClockwise size={16} className="text-amber-600 shrink-0" />
          <h2 className="text-[13px] font-semibold text-zinc-800">送信履歴</h2>
          <span className="text-[10.5px] text-zinc-400">直近 50 件・保存 90 日</span>
        </div>
        <div className="flex items-center gap-1.5">
          {[
            { v: '', label: 'すべて' },
            { v: 'eng_progress', label: '進捗レポート' },
            { v: 'project_weekly', label: '週間プロジェクト' },
            { v: 'daily_report', label: '日報' },
          ].map((f) => (
            <button key={f.v} type="button" onClick={() => setFilter(f.v)}
              data-testid={`digest-history-filter-${f.v || 'all'}`}
              className={`text-[10.5px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${
                filter === f.v
                  ? 'bg-teal-600 border-teal-600 text-white'
                  : 'bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50'
              }`}>{f.label}</button>
          ))}
        </div>
      </div>
      {rows === null && <p className="px-4 py-6 text-center text-[12px] text-zinc-400">読み込み中…</p>}
      {rows !== null && rows.length === 0 && (
        <p className="px-4 py-8 text-center text-[12px] text-zinc-400" data-testid="digest-history-empty">
          送信履歴はまだありません。ダイジェストを生成して送信すると、ここに記録されます。
        </p>
      )}
      {rows !== null && rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-[12px]">
            <thead className="bg-zinc-50 text-[10.5px] uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="text-left px-4 py-2 font-semibold whitespace-nowrap">種類</th>
                <th className="text-left px-4 py-2 font-semibold whitespace-nowrap">対象期間</th>
                <th className="text-left px-4 py-2 font-semibold whitespace-nowrap">状態</th>
                <th className="text-left px-4 py-2 font-semibold whitespace-nowrap">生成者</th>
                <th className="text-left px-4 py-2 font-semibold whitespace-nowrap">確認者</th>
                <th className="text-left px-4 py-2 font-semibold whitespace-nowrap">送信者</th>
                <th className="text-left px-4 py-2 font-semibold whitespace-nowrap">送信時刻</th>
                <th className="text-left px-4 py-2 font-semibold">宛先</th>
                <th className="text-left px-4 py-2 font-semibold whitespace-nowrap">添付</th>
                <th className="text-right px-4 py-2 font-semibold whitespace-nowrap">操作</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const s = STATUS_STYLE[r.status] || STATUS_STYLE.draft;
                const { Icon } = s;
                const canSend = r.status === 'pending_confirmation' || r.status === 'failed';
                return (
                  <tr key={r.id} className="border-t border-zinc-200 hover:bg-amber-50/30" data-testid={`digest-history-row-${r.id}`}>
                    <td className="px-4 py-2.5 whitespace-nowrap text-zinc-700">{KIND_LABEL[r.kind] || r.kind}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap tabular-nums text-zinc-600">{r.period_start} 〜 {r.period_end}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px] font-semibold ${s.cls}`}>
                        <Icon size={11} weight="fill" /> {s.label}
                      </span>
                      {r.status === 'failed' && r.error_message && (
                        <div className="mt-1 text-[10px] text-rose-600 max-w-[220px] truncate" title={r.error_message}>
                          <Warning size={10} className="inline mr-0.5" /> {r.error_message}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-zinc-600 truncate max-w-[120px]">{r.generated_by?.name || '—'}</td>
                    <td className="px-4 py-2.5 text-zinc-600 truncate max-w-[120px]">{r.confirmed_by?.name || '—'}</td>
                    <td className="px-4 py-2.5 text-zinc-600 truncate max-w-[120px]">{r.sent_by?.name || '—'}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap tabular-nums text-zinc-600">{fmtDT(r.sent_at)}</td>
                    <td className="px-4 py-2.5">
                      <span className="text-[10.5px] text-zinc-500" title={(r.recipients || []).join(', ')}>
                        {(r.recipients || []).length} 名
                      </span>
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      {r.attachment ? (
                        <span className="inline-flex items-center gap-1 text-[10.5px] text-zinc-600" title={r.attachment.filename}>
                          <Paperclip size={11} /> PDF
                        </span>
                      ) : <span className="text-[10.5px] text-zinc-300">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      {canSend && (
                        <button type="button" onClick={() => openDialog(r)}
                          data-testid={`digest-history-send-${r.id}`}
                          className="inline-flex items-center gap-1 rounded-lg border border-teal-300 bg-teal-50 px-2.5 py-1 text-[10.5px] font-semibold text-teal-700 hover:bg-teal-100">
                          <PaperPlaneTilt size={11} /> プレビュー・送信
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <ConfirmSendDialog
        run={dlgRun}
        onClose={() => setDlgRun(null)}
        onSent={() => { setDlgRun(null); load(); }}
      />
    </div>
  );
}
